import { Box, Button, Typography } from "@mui/material";
import { Fragment, lazy, Suspense, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AlertDialogSlide from "../../../../components/Dialog";
import UpdateCommentText from "../../../../components/UpdateComment";
import UserAvatar from "../../../../components/UserAvatar";
import {
  deleteCommentAction,
  updateCommentAction,
} from "../../../../store/actions/video/comments";

const Replies = lazy(() => import("./Replies"));

export default function CommentItem({
  image,
  name,
  comment,
  star,
  likes,
  id,
  data,
  color,
}) {
  const dispatch = useDispatch();

  const [delOpen, setDelOpen] = useState(false);
  const [openReply, setOpenReply] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const user = useSelector((s) => s.auth.user);

  const openDel = () => setDelOpen(true);
  const closeDel = () => setDelOpen(false);

  const openEdit = () => setEditOpen(true);
  const closeEdit = () => setEditOpen(false);

  const deleteComment = useCallback(() => {
    deleteCommentAction(id)(dispatch);
    closeDel();
  }, [id, dispatch]);

  const handleUpdateComments = useCallback(
    (values, callback) => {
      setEditLoading(true);
      const { text } = values;
      const cb = (err) => {
        if (callback) callback(err);
        setEditLoading(false);
        if (!err) closeEdit();
      };
      updateCommentAction({ cb, data: { id: id, text } })(dispatch);
    },
    [id, dispatch]
  );

  return (
    <Box>
      {editOpen && (
        <Suspense fallback={<Fragment />}>
          <UpdateCommentText
            open={editOpen}
            handleClose={closeEdit}
            comment={data}
            handleUpdateComments={handleUpdateComments}
            loading={editLoading}
          />
        </Suspense>
      )}
      {delOpen && (
        <AlertDialogSlide
          clickOpen={openDel}
          open={delOpen}
          onConfirm={deleteComment}
          close={closeDel}
          okText={"delete"}
          cancelText={"cancel"}
          title={"delete comment"}
          subTitle={"are you sure, you want to delete this comment ?"}
        />
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          padding: "1em",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "start",
            alignItems: "start",
            gap: "0.5em",
          }}
        >
          <UserAvatar
            src={image}
            alt={name}
            color={color}
            sx={{ cursor: "pointer" }}
          />
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              variant="body1"
              sx={{
                color: "#323232",
              }}
            >
              {name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#6C6C6C",
              }}
            >
              {comment}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                transition: "0.5s all",
                fill: "#c8c8c8",
                stroke: "#c8c8c8",
                "&:hover": {
                  fill: "#25AAE1",
                  cursor: "pointer",
                  stroke: "#25AAE1",
                },
              }}
            >
              {star}
            </Box>
            <Typography
              variant="caption"
              sx={{ color: "#7A7A7A", pointerEvents: "none" }}
            >
              {likes}
            </Typography>
          </Box>

          <Button
            size="small"
            sx={{
              color: "#323232",
              textDecoration: "underline",
              textTransform: "none",
              transition: "0.5s all",
              "&:hover": {
                textDecoration: "underline",
                color: "#25AAE1",
              },
            }}
            onClick={() => !openReply && setOpenReply(true)}
          >
            Reply
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }}
      >
        <Button
          size="small"
          sx={{
            color: "#323232",
            textTransform: "none",
            transition: "0.5s all",
            "&:hover": {
              color: "#25AAE1",
            },
          }}
          onClick={openEdit}
        >
          Edit
        </Button>
        <Button
          size="small"
          sx={{
            color: "#323232",
            textTransform: "none",
            transition: "0.5s all",
            "&:hover": {
              color: "#25AAE1",
            },
          }}
          onClick={openDel}
        >
          Delete
        </Button>
      </Box>
      <Box sx={{ px: "1em" }}>
        <Suspense fallback={<Fragment />}>
          <Replies
            user={user}
            comment={{
              id: data?.id,
              replies: data?.replies || [],
              repliesCount: data?.repliesCount,
            }}
            openReply={openReply}
          />
        </Suspense>
      </Box>
    </Box>
  );
}
